import { GroupedInvoiceFileProcessingStatusDto, InvoiceFileProcessingStatusDto } from 'src/generated/services/TFinancialApi';
import { processingStatuses } from '../uploadInvoice/configs';
import { isStatusOther, isStatusUnderReview, isStatusProcessing } from '../uploadInvoice/uploadUtils';

/**
 * Utility functions for notification data processing and filtering
 *
 * These functions extract the complex filtering logic from components
 * and hooks, making them reusable and easier to test.
 */

/**
 * Filters data to get provider lists (items with service provider codes)
 */
export function filterProviderLists(data: GroupedInvoiceFileProcessingStatusDto[]): GroupedInvoiceFileProcessingStatusDto[] {
  if (!data) return [];

  return data.filter((item: GroupedInvoiceFileProcessingStatusDto) => item?.serviceProviderCode);
}

/**
 * Filters data to get under review lists
 * Items without service provider codes that are under review
 */
export function filterUnderReviewLists(data: GroupedInvoiceFileProcessingStatusDto[]): GroupedInvoiceFileProcessingStatusDto[] {
  if (!data) return [];

  return data.filter((item: GroupedInvoiceFileProcessingStatusDto) => {
    const isUnderReview = item?.invoiceGroups?.some(
      (invoiceGroup) =>
        invoiceGroup.invoiceFileProcessingStatusList?.find((statusItem: InvoiceFileProcessingStatusDto) => {
          // Check if invoiceStatus is null or undefined
          const hasNoInvoiceStatus = !statusItem?.invoiceStatus;
          // Check if fileResolutionStatus is in the review states
          const isInReviewStatus = statusItem?.fileResolutionStatus && ['REVIEW_REQUIRED', 'UNDER_REVIEW'].includes(statusItem?.fileResolutionStatus);

          return hasNoInvoiceStatus && isInReviewStatus;
        })
    );
    return item?.serviceProviderCode === null && isUnderReview;
  });
}

/**
 * Filters data to get "other" lists
 * Items without service provider codes that have:
 * - Invalid file statuses (from invalidFileStatuses config: not an invoice, missing fields, duplicate, etc.)
 * - Under review status
 */
export function filterOtherLists(data: GroupedInvoiceFileProcessingStatusDto[]): GroupedInvoiceFileProcessingStatusDto[] {
  if (!data) return [];

  return data.filter((item: GroupedInvoiceFileProcessingStatusDto) => {
    const hasOtherStatus = item?.invoiceGroups?.some(
      (invoiceGroup) => invoiceGroup.invoiceFileProcessingStatusList?.find((statusItem: InvoiceFileProcessingStatusDto) => isStatusOther(statusItem))
    );
    return item?.serviceProviderCode === null && hasOtherStatus;
  });
}

/**
 * Filters data to get processing lists
 * Items without service provider codes that are processing (but not under review or other)
 */
export function filterProcessingLists(
  data: GroupedInvoiceFileProcessingStatusDto[],
  errorProcessingStatuses: string[] = []
): GroupedInvoiceFileProcessingStatusDto[] {
  if (!data) return [];

  return data.filter((item: GroupedInvoiceFileProcessingStatusDto) => {
    const isProcessing = item?.invoiceGroups?.some(
      (invoiceGroup) =>
        invoiceGroup.invoiceFileProcessingStatusList?.find((statusItem: InvoiceFileProcessingStatusDto) => {
          // Check if it's under review first
          const hasNoInvoiceStatus = !statusItem?.invoiceStatus;
          const isInReviewStatus = statusItem?.fileResolutionStatus && ['REVIEW_REQUIRED', 'UNDER_REVIEW'].includes(statusItem?.fileResolutionStatus);
          const isUnderReview = hasNoInvoiceStatus && isInReviewStatus;

          // Only include in processing if it's NOT under review
          return processingStatuses.includes(statusItem?.status) && !errorProcessingStatuses.includes(statusItem?.status) && !isUnderReview;
        })
    );
    return item?.serviceProviderCode === null && isProcessing;
  });
}

/**
 * Processes raw notification data and returns all filtered lists
 * This is the main function that combines all filtering logic
 * Optimized to filter data in a single pass for better performance
 */
export function processNotificationData(rawData: GroupedInvoiceFileProcessingStatusDto[], errorProcessingStatuses: string[] = []) {
  if (!rawData || rawData.length === 0) {
    return {
      providerLists: [],
      otherLists: [],
      underReviewLists: [],
      processingLists: [],
      allData: [],
    };
  }

  // Single-pass filtering for better performance
  const providerLists: GroupedInvoiceFileProcessingStatusDto[] = [];
  const otherLists: GroupedInvoiceFileProcessingStatusDto[] = [];
  const underReviewLists: GroupedInvoiceFileProcessingStatusDto[] = [];
  const processingLists: GroupedInvoiceFileProcessingStatusDto[] = [];

  for (const item of rawData) {
    // Provider lists - items with service provider codes (Priority 1)
    if (item?.serviceProviderCode) {
      providerLists.push(item);
    } else if (item?.serviceProviderCode === null) {
      // For items without service provider codes, check in priority order:
      // Priority 2: Other (resolution issues + under review)
      const hasOtherItem = item?.invoiceGroups?.some(
        (invoiceGroup) => invoiceGroup.invoiceFileProcessingStatusList?.some((statusItem) => isStatusOther(statusItem))
      );

      if (hasOtherItem) {
        otherLists.push(item);
      } else {
        // Priority 3: Under Review (deprecated - now included in Other)
        // Kept for backward compatibility, but should be empty
        const hasUnderReviewItem = item?.invoiceGroups?.some(
          (invoiceGroup) => invoiceGroup.invoiceFileProcessingStatusList?.some((statusItem) => isStatusUnderReview(statusItem))
        );

        if (hasUnderReviewItem) {
          underReviewLists.push(item);
        } else {
          // Priority 4: Processing
          const hasProcessingItem = item?.invoiceGroups?.some(
            (invoiceGroup) => invoiceGroup.invoiceFileProcessingStatusList?.some((statusItem) => isStatusProcessing(statusItem, errorProcessingStatuses))
          );

          if (hasProcessingItem) {
            processingLists.push(item);
          }
        }
      }
    }
  }

  return {
    providerLists,
    otherLists,
    underReviewLists,
    processingLists,
    allData: rawData,
  };
}

/**
 * Gets the count of items in each category
 */
export function getNotificationCounts(
  providerLists: GroupedInvoiceFileProcessingStatusDto[],
  otherLists: GroupedInvoiceFileProcessingStatusDto[],
  underReviewLists: GroupedInvoiceFileProcessingStatusDto[],
  processingLists: GroupedInvoiceFileProcessingStatusDto[]
) {
  const getItemCount = (lists: GroupedInvoiceFileProcessingStatusDto[]) =>
    lists.reduce((count, serviceProvider) => {
      const invoiceGroupsCount =
        serviceProvider.invoiceGroups?.reduce((groupCount, invoiceGroup) => groupCount + (invoiceGroup.invoiceFileProcessingStatusList?.length || 0), 0) || 0;
      return count + invoiceGroupsCount;
    }, 0);

  return {
    providerCount: getItemCount(providerLists),
    otherCount: getItemCount(otherLists),
    underReviewCount: getItemCount(underReviewLists),
    processingCount: getItemCount(processingLists),
    totalCount: getItemCount(providerLists) + getItemCount(otherLists) + getItemCount(underReviewLists) + getItemCount(processingLists),
  };
}
